import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";
import { formatHeat, formatShortDate } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "feature" | "default" | "list" | "compact";
  rank?: number;
}

const CategoryTag = ({ slug, solid = false }: { slug: string; solid?: boolean }) => {
  const category = getCategory(slug);
  if (!category) return null;
  const className = solid
    ? "bg-primary-500 text-background-50"
    : "bg-primary-100 text-primary-800";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide ${className}`}
    >
      {category.name}
    </span>
  );
};

const MetaRow = ({ article }: { article: Article }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
    <span className="whitespace-nowrap">{article.publishedAt}</span>
    <span className="flex items-center gap-1 whitespace-nowrap">
      <i className="ri-fire-line text-primary-500" />
      {formatHeat(article.heat)} 热度
    </span>
    <span className="whitespace-nowrap">约 {article.readMinutes} 分钟读完</span>
  </div>
);

export default function ArticleCard({ article, variant = "default", rank }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3 cursor-pointer"
      >
        <span
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
            rank && rank <= 3
              ? "bg-primary-500 text-background-50"
              : "bg-background-200 text-foreground-700"
          }`}
        >
          {rank ?? ""}
        </span>
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <div className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.publishedAt}</span>
            <span className="text-foreground-300">·</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" />
              {formatHeat(article.heat)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid grid-cols-1 gap-4 border-b border-background-200 py-5 transition-colors hover:border-primary-300 sm:grid-cols-[200px_1fr] md:gap-6 cursor-pointer"
      >
        <div className="relative h-40 w-full overflow-hidden rounded-lg bg-background-100 sm:h-[132px]">
          <img
            src={article.cover}
            alt={article.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <div className="mb-2 flex items-center gap-2">
            <CategoryTag slug={article.category} />
            <span className="text-xs text-foreground-400">{formatShortDate(article.publishedAt)}</span>
          </div>
          <h3 className="mb-2 line-clamp-2 text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600 md:text-lg">
            {article.title}
          </h3>
          <p className="mb-3 line-clamp-2 text-sm text-foreground-600">{article.summary}</p>
          <MetaRow article={article} />
        </div>
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative flex min-h-[360px] flex-col justify-end overflow-hidden rounded-lg bg-foreground-950 p-6 md:min-h-[440px] md:p-8 cursor-pointer"
      >
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/60 to-transparent" />
        <div className="relative z-10">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <CategoryTag slug={article.category} solid />
            <span className="rounded-full bg-background-50/15 px-2.5 py-0.5 text-[11px] text-background-50 backdrop-blur">
              焦点大瓜
            </span>
          </div>
          <h3 className="mb-3 text-xl font-bold leading-snug text-background-50 md:text-2xl lg:text-3xl">
            {article.title}
          </h3>
          <p className="mb-4 line-clamp-2 max-w-2xl text-sm text-background-200 md:text-base">
            {article.summary}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-300">
            <span className="whitespace-nowrap">{article.publishedAt}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-fire-fill text-accent-500" />
              {formatHeat(article.heat)} 热度
            </span>
            <span className="whitespace-nowrap">{article.author}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 cursor-pointer"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-100 md:h-48">
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-2">
          <CategoryTag slug={article.category} />
        </div>
        <h3 className="mb-2 line-clamp-2 text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        <p className="mb-4 line-clamp-3 flex-1 text-sm text-foreground-600">{article.summary}</p>
        <MetaRow article={article} />
      </div>
    </Link>
  );
}